import { clientCounts, countsLine } from '@boViews/shared/clientCounts';
import { formatList, languageName, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import {
    faArrowTurnUp,
    faCircleExclamation,
    faEye,
    faEyeSlash,
    faLockKeyhole,
    faLockKeyholeOpen,
} from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import ChoiceCard from '@sharedComponents/ChoiceCard';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import Typography from '@sharedComponents/Typography';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RESTRICTED } from '../accessRules';
import OverrideBadge from '../OverrideBadge';
import { overriddenLanguages, targetKey, transferAccess, transferOptions } from '../overrideRules';
import RowValues, { DisplayValue, ValueLine } from '../RowValues';

import * as style from './style.module.css';

const noTransfer = async () => false;

// Sentence telling how the transfer changes the access of the chosen client `name` and of its platforms.
const clientAccessSentence = (t, restricted, gains, name) => {
    if (restricted) {
        return gains
            ? t('{{name}} and its platforms will be authorized.', { name })
            : t('{{name}} and its platforms will no longer see the collection.', { name });
    }
    return gains
        ? t('The collection will be visible again for {{name}} and its platforms.', { name })
        : t('The collection will be hidden for {{name}} and all its platforms.', { name });
};

// Sentence telling how the transfer changes the access of the platform `url`, under the row of `client`.
const platformAccessSentence = (t, restricted, gains, url, client) => {
    if (restricted) {
        return gains
            ? t('{{url}} will be authorized: the row of {{client}} applies to it.', { url, client })
            : t('{{url}} will no longer see the collection: the row of {{client}} does not authorize it.', { url, client });
    }
    return gains
        ? t('{{url}} will be visible again: the row of {{client}} applies to it.', { url, client })
        : t('{{url}} will be hidden: the row of {{client}} applies to it.', { url, client });
};

const accessIcon = (restricted, gains) => {
    if (restricted) return gains ? faLockKeyholeOpen : faLockKeyhole;
    return gains ? faEye : faEyeSlash;
};

/**
 * Body of the modal moving the override of the platform row `row` to a client, chosen among those of
 * transferOptions: the client of the platform, selected first, and the parent of a sub-client. It shows the
 * values of the platform row, what the transfer changes on the current row of the chosen client, how it
 * changes the access, under the `visibility`, of that client and of the platform (see transferAccess) and, for
 * a parent, that the own row of the sub-client keeps applying to the platform. `languages` are the catalogue
 * languages, which the badges count against; when the two badges of a changed text read the same, its changed
 * languages follow them.
 * `onTransfer(target)` resolves to whether the override was transferred, and must not reject: once done, the
 * modal closes itself through `onClose`, its buttons left disabled as it stays mounted while it fades out;
 * otherwise they are enabled again. A transfer that settles once this body is unmounted, another modal having
 * replaced it, does nothing. The client cannot be changed while the transfer runs.
 */
const TransferModal = ({ row, overrides = [], visibility, languages = [], onTransfer = noTransfer, onClose = () => {} }) => {
    const { t, i18n } = useTranslation('bo');
    const options = transferOptions(row, overrides);
    const [selectedKey, setSelectedKey] = useState(() => targetKey(options[0].target));
    const [sending, setSending] = useState(false);
    const mounted = useRef(true);
    const groupLabelId = useId();

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const { url } = row.platform;
    const locale = uiLocale(i18n);
    const restricted = visibility === RESTRICTED;
    const total = languages.length;
    const option = options.find((candidate) => targetKey(candidate.target) === selectedKey) ?? options[0];
    const { name } = option.target;
    // The visibility of a restricted collection is its access, told by the access messages rather than the changes.
    const changes = restricted ? option.changes.filter(({ field }) => field !== 'visible') : option.changes;
    const access = transferAccess(row, option, overrides, visibility);
    const accessChanges = [
        access.client.before !== access.client.after && {
            key: 'client',
            gains: access.client.after,
            text: clientAccessSentence(t, restricted, access.client.after, name),
        },
        access.platform.before !== access.platform.after && {
            key: 'platform',
            gains: access.platform.after,
            text: platformAccessSentence(t, restricted, access.platform.after, url, access.platform.appliedBy),
        },
    ].filter(Boolean);

    const labelOf = (field) => {
        if (field === 'title') return t('Title');
        if (field === 'description') return t('Description');
        return t('Display');
    };

    const valueOf = (field, value) =>
        field === 'visible' ? <DisplayValue visible={value} /> : <OverrideBadge text={value} total={total} />;

    // Two badges of a text read the same when they count as many overridden languages.
    const sameBadges = (from, to) => overriddenLanguages(from).length === overriddenLanguages(to).length;

    const select = (key) => {
        if (!sending) setSelectedKey(key);
    };

    const transfer = async () => {
        setSending(true);
        const done = await onTransfer(option.target);
        if (!mounted.current) return;
        if (done) onClose();
        else setSending(false);
    };

    return (
        <FlexBox flexDirection="column" gap="m">
            <Typography color="neutral-secondary">
                {t(
                    'The override of {{name}} moves to a client: it will apply to all its platforms, except those that have their own row. The row of {{name}} will be deleted.',
                    { name: url },
                )}
            </Typography>
            <RowValues row={row} total={total} withDisplay={!restricted} />
            <FlexBox flexDirection="column" gap="s">
                <Typography id={groupLabelId} fontWeight="medium" color="neutral-primary">
                    {t('Client receiving the override')}
                </Typography>
                <FlexBox role="radiogroup" aria-labelledby={groupLabelId} gap="s" flexWrap="wrap">
                    {options.map((candidate) => {
                        const key = targetKey(candidate.target);
                        return (
                            <ChoiceCard
                                key={key}
                                title={candidate.target.name}
                                description={candidate.isParent ? t('Parent client') : countsLine(t, clientCounts(candidate.client))}
                                selected={key === targetKey(option.target)}
                                onSelect={() => select(key)}
                            />
                        );
                    })}
                </FlexBox>
            </FlexBox>
            <FlexBox flexDirection="column" gap="s" aria-live="polite">
                {option.currentRow ? (
                    <FlexBox flexDirection="column" gap="xs">
                        <Typography>{t('The current override of {{name}} will be replaced.', { name })}</Typography>
                        {changes.length > 0 ? (
                            <FlexBox role="list" flexDirection="column" gap="xs">
                                {changes.map(({ field, from, to, languages: changed }) => (
                                    <ValueLine key={field} role="listitem" label={labelOf(field)}>
                                        {valueOf(field, from)}
                                        <Typography span color="neutral-tertiary">
                                            →
                                        </Typography>
                                        {valueOf(field, to)}
                                        {changed.length > 0 && sameBadges(from, to) && (
                                            <Typography span fontSize="s" color="neutral-tertiary">
                                                {t('text changed: {{languages}}', {
                                                    languages: formatList(
                                                        changed.map((code) => languageName(code, locale)),
                                                        locale,
                                                    ),
                                                })}
                                            </Typography>
                                        )}
                                    </ValueLine>
                                ))}
                            </FlexBox>
                        ) : (
                            <Typography fontSize="s" color="neutral-secondary">
                                {t('Its values stay the same.')}
                            </Typography>
                        )}
                    </FlexBox>
                ) : (
                    <Typography>{t('{{name}} has no override yet.', { name })}</Typography>
                )}
                {accessChanges.map(({ key, gains, text }) => (
                    <InformationMessage key={key} status={gains ? 'info' : 'warning'} icon={accessIcon(restricted, gains)} text={text} />
                ))}
                {option.shadowedBy && (
                    <InformationMessage
                        status="warning"
                        icon={faCircleExclamation}
                        text={t('{{subClient}} has its own row: it is the one that will apply to {{url}}, not the one of {{parent}}.', {
                            subClient: option.shadowedBy,
                            url,
                            parent: name,
                        })}
                    />
                )}
            </FlexBox>
            <FlexBox justifyContent="flex-end" alignItems="center" gap="s" flexWrap="wrap" className={style.footer}>
                <Button outline label={t('Cancel')} disabled={sending} action={onClose} />
                <Button
                    icon={faArrowTurnUp}
                    label={t('Transfer to {{name}}', { name })}
                    disabled={sending}
                    isLoading={sending}
                    action={() => {
                        transfer();
                    }}
                />
            </FlexBox>
        </FlexBox>
    );
};

export default TransferModal;
